import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { getHostClass, urlOnHost } from "@/lib/host";
import Seo from "@/components/Seo";
import { useFormAnalytics } from "@/lib/formAnalytics";
import { usePricing, planName } from "@/lib/pricing";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Iteration 31: role is fixed by host. Main site → merchant only, app → shopper only.
  const host = getHostClass();
  const forcedRole = host === "app" ? "user" : host === "main" ? "merchant" : null;
  const initialRole = forcedRole || (params.get("role") === "merchant" ? "merchant" : "user");
  const [role, setRole] = useState(initialRole);
  const fa = useFormAnalytics("merchant_signup", { enabled: role === "merchant" });
  const canToggleRole = forcedRole === null; // only preview/dev shows the toggle
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Loop P4-referral · capture ?ref=CODE for merchant sign-ups.
  const referralCode = (params.get("ref") || "").trim().toUpperCase().slice(0, 32) || null;
  // CYANNECT-PRICING-FINAL · merchants pick Basic / Starter / Pro (card required) right after signup.
  const pricing = usePricing();
  const finalPricing = !!pricing?.final_active;
  const chosenPlan = ["basic", "starter", "growth"].includes(params.get("plan")) ? params.get("plan") : null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!termsAccepted) {
      setError("Please review and accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    const res = await register({
      email, password, name, role,
      business_name: businessName,
      terms_accepted: true,
      referral_code: referralCode || undefined,
    });
    setLoading(false);
    if (res.ok) {
      if (role === "merchant") fa.submitted();
      toast.success("Account created");
      if (role === "merchant") navigate(finalPricing ? `/merchant/billing${chosenPlan ? `?plan=${chosenPlan}` : ""}` : "/merchant");
      else navigate("/dashboard");
    } else setError(res.error);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 py-16 relative">
      <Seo
        title={role === "merchant" ? "Merchant sign-up" : "Create your account"}
        description={role === "merchant" ? "Create your Cyannect merchant account and get ready for Starter or Growth activation. Sign up in under a minute — no card required." : "Join Cyannect to discover verified Canadian coupons, cashback offers and flash sales from your favourite brands."}
        path="/register"
        noindex
      />
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="blob blob-mint blob-float w-[500px] h-[500px] top-10 -left-40" />
        <div className="blob blob-cyan blob-float w-[500px] h-[500px] bottom-10 -right-40" style={{ animationDelay: "4s" }} />
      </div>
      <div className="max-w-md mx-auto relative z-10">
        <div className="glass-strong rounded-[28px] w-full p-8 fade-up">
        <img src="/cyannect-logo.png" alt="Cyannect" className="h-16 w-auto mx-auto mb-2" />
        <div className="pf-chip pf-chip-mint mx-auto w-fit">Join Cyannect</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-4 text-center text-slate-900">Create account</h1>

        <form onSubmit={submit} className="mt-6" data-testid="register-form" {...(role === "merchant" ? fa.formProps : {})}>
          {canToggleRole ? (
            <div className="grid grid-cols-2 rounded-full glass p-1">
              <button type="button" onClick={() => setRole("user")} className={`py-2 rounded-full text-sm font-semibold transition-all ${role === "user" ? "pf-btn-primary" : "text-slate-600"}`} data-testid="role-user">Shopper</button>
              <button type="button" onClick={() => setRole("merchant")} className={`py-2 rounded-full text-sm font-semibold transition-all ${role === "merchant" ? "pf-btn-primary" : "text-slate-600"}`} data-testid="role-merchant">Merchant</button>
            </div>
          ) : (
            <div className="rounded-full glass px-4 py-2 text-center text-xs uppercase tracking-widest text-cyan-700 font-semibold" data-testid={`role-locked-${role}`}>
              {role === "merchant" ? "Merchant sign-up" : "Shopper sign-up"}
              <span className="normal-case text-slate-500 tracking-normal ml-2">
                · {role === "merchant"
                    ? <>Shopping? <a href={urlOnHost("app", "/register")} className="underline hover:text-cyan-800">Use the app →</a></>
                    : <>Have a brand? <a href={urlOnHost("main", "/register")} className="underline hover:text-cyan-800">List on cyannect.com →</a></>}
              </span>
            </div>
          )}

          {role === "merchant" && (
            <Link
              to="/faq?tab=merchant"
              target="_blank"
              rel="noopener"
              className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-cyan-100 bg-cyan-50/60 px-4 py-3 text-sm text-slate-700 hover:border-cyan-300 transition-colors"
              data-testid="register-merchant-faq-link"
            >
              <span><span className="font-semibold text-slate-900">Questions about pricing?</span> Read the Merchant FAQ first.</span>
              <span className="text-cyan-700 font-semibold whitespace-nowrap">Merchant FAQ →</span>
            </Link>
          )}

          <div className="mt-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Your name</label>
              <input required value={name} onChange={(e) => setName(e.target.value)} className="pf-input w-full px-4 py-3 text-sm" data-testid="register-name" />
            </div>
            {role === "merchant" && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Business name</label>
                <input required value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="pf-input w-full px-4 py-3 text-sm" data-testid="register-business" />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pf-input w-full px-4 py-3 text-sm" data-testid="register-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Password</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="pf-input w-full px-4 py-3 text-sm" data-testid="register-password" />
            </div>
          </div>

          {error && <div className="mt-4 text-sm text-red-600 bg-red-50/70 backdrop-blur border border-red-200 rounded-xl px-4 py-2" data-testid="register-error">{error}</div>}

          {referralCode && role === "merchant" && (
            <div
              className="mt-4 flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2"
              data-testid="register-referral-banner"
            >
              <span className="font-semibold">Referral applied</span>
              <span className="text-emerald-700">
                Code <span className="font-mono">{referralCode}</span> — both accounts get a plan discount after you subscribe.
              </span>
            </div>
          )}

          <label className="mt-5 flex items-start gap-3 text-sm text-slate-700 cursor-pointer select-none" data-testid="register-terms-label">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              data-testid="register-terms-checkbox"
              required
            />
            <span>
              I agree to Cyannect's{" "}
              <Link to="/terms" target="_blank" rel="noopener" className="text-cyan-700 font-medium hover:underline" data-testid="register-terms-link">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" rel="noopener" className="text-cyan-700 font-medium hover:underline" data-testid="register-privacy-link">
                Privacy Policy
              </Link>.
            </span>
          </label>

          <button type="submit" disabled={loading || !termsAccepted} className="pf-btn-primary w-full mt-6 py-3 rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed" data-testid="register-submit">
            {loading ? "Creating account..." : role === "merchant" ? "Create merchant account" : "Create account"}
          </button>

          <div className="mt-6 text-sm text-slate-600 text-center">
            Already have an account? <Link to="/login" className="text-cyan-700 font-semibold hover:underline">Log in</Link>
          </div>
        </form>
        </div>
      </div>

      {role === "merchant" && finalPricing && (
        <div className="max-w-3xl mx-auto mt-12 relative z-10" data-testid="merchant-signup-plans-final">
          <div className="rounded-2xl bg-white/85 backdrop-blur border border-slate-200 p-6 text-center">
            <div className="text-slate-800 text-lg font-medium" data-testid="merchant-signup-chosen-plan">
              {chosenPlan ? `Next: activate ${planName(pricing, chosenPlan, chosenPlan)}` : "Next: pick Basic, Starter or Pro"}
            </div>
            <p className="text-slate-600 text-sm mt-2 max-w-xl mx-auto">
              After you create your account you'll choose a plan and add a card. {planName(pricing, "basic", "Basic")} has no monthly fee —
              the card covers performance fees on verified sales and any add-ons.
            </p>
            <Link to="/pricing" className="inline-block mt-4 text-cyan-700 text-sm font-medium hover:underline" data-testid="merchant-signup-pricing-link">
              Compare plans →
            </Link>
          </div>
        </div>
      )}

      {role === "merchant" && pricing !== undefined && !finalPricing && (
        <div className="max-w-3xl mx-auto mt-12 relative z-10" data-testid="merchant-signup-plans-notice">
          <div className="rounded-2xl bg-white/85 backdrop-blur border border-slate-200 p-6 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[11px] uppercase tracking-widest text-amber-800 font-semibold mb-3">
              Plans launching soon
            </div>
            <div className="text-slate-800 text-lg font-medium">
              Cyannect merchant plans are launching soon.
            </div>
            <p className="text-slate-600 text-sm mt-2 max-w-xl mx-auto">
              Create your account today — we'll follow up with your Starter or Growth activation once billing is live.
            </p>
            <Link to="/pricing" className="inline-block mt-4 text-cyan-700 text-sm font-medium hover:underline">
              See merchant pricing →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
